import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type HovercardAttributes, hovercardAttributesForActor} from '@github-ui/hovercards'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {ProfileReference} from '@github-ui/profile-reference'
import {useFeatureFlags} from '@github-ui/react-core/use-feature-flag'
import {Link, RelativeTime, Truncate} from '@primer/react'
import {graphql, type PreloadedQuery, useFragment} from 'react-relay'

import {LABELS} from '../constants/labels'
import {TEST_IDS} from '../constants/test-ids'
import {VALUES} from '../constants/values'
import type {CheckRunStatus$data} from './__generated__/CheckRunStatus.graphql'
import type {CheckRunStatusFromPullRequest$key} from './__generated__/CheckRunStatusFromPullRequest.graphql'
import type {IssuePullRequestDescription$key} from './__generated__/IssuePullRequestDescription.graphql'
import type {IssueRowSecondaryQuery} from './__generated__/IssueRowSecondaryQuery.graphql'
import {CheckRunStatus, LazyCheckRunStatus} from './CheckRunStatus'
import styles from './IssueItem.module.css'
import pullRequestDescriptionStyles from './IssuePullRequestDescription.module.css'
import {MilestoneMetadata} from './MilestoneMetadata'
import {LazyReviewDecision, ReviewDecision} from './ReviewDecision'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const descriptionFragment = graphql`
  fragment IssuePullRequestDescription on IssueOrPullRequest
  @argumentDefinitions(
    includeGitData: {type: "Boolean", defaultValue: true}
    includeMilestone: {type: "Boolean!", defaultValue: true}
  ) {
    ... on Issue {
      createdAt
      updatedAt
      closed
      closedAt

      author {
        login
        resourcePath
        __typename
        ... on Bot {
          isCopilot
        }
        ... on User {
          name
        }
        ... on EnterpriseUserAccount {
          name
        }
        ... on Mannequin {
          name
        }
      }
      number
      stateReason
      milestone @include(if: $includeMilestone) {
        title
        url
      }
    }

    ... on PullRequest {
      createdAt
      updatedAt
      closed
      closedAt
      author {
        login
        resourcePath
        __typename
        ... on Bot {
          isCopilot
        }
        ... on User {
          name
        }
        ... on EnterpriseUserAccount {
          name
        }
        ... on Mannequin {
          name
        }
      }
      number
      milestone @include(if: $includeMilestone) {
        title
        url
      }
      reviewDecision @include(if: $includeGitData)
    }
  }
`

type DescriptionProps = {
  dataKey: IssuePullRequestDescription$key | CheckRunStatusFromPullRequest$key
  metadataRef?: PreloadedQuery<IssueRowSecondaryQuery> | null
  repositoryOwner: string
  repositoryName: string
  showRepository?: boolean
  sortingItemSelected?: string
  statusCheckRollup?: NonNullable<CheckRunStatus$data['statusCheckRollup']>
  getAuthorHref?: (login: string) => string
  id: string
  includeGitDataFromMainQuery?: boolean
}

export function IssuePullRequestDescription({dataKey, repositoryOwner, repositoryName, ...props}: DescriptionProps) {
  const {number} = useFragment(descriptionFragment, dataKey as IssuePullRequestDescription$key)
  const nameWithOwner = `${repositoryOwner}/${repositoryName}`
  const ariaLabels: {[id: string]: string} = {
    number: `number ${number} `,
    repo: `In ${nameWithOwner};`,
  }

  const defaultRepositoryRender = (
    <div className={styles.defaultRepoContainer}>
      <span>{props.showRepository ? nameWithOwner : ''}</span>
      <span className="sr-only">{ariaLabels.number}</span>
    </div>
  )

  const defaultMetaRender = (
    <span className={styles.defaultNumberDescription}>
      <span>#{number}</span>
      &nbsp;
      <span className="sr-only">{ariaLabels.repo}</span>
    </span>
  )

  return (
    <IssuePullRequestDescriptionItem
      dataKey={dataKey}
      defaultRepositoryRender={defaultRepositoryRender}
      defaultMetaRender={defaultMetaRender}
      nameWithOwner={nameWithOwner}
      repositoryOwner={repositoryOwner}
      repositoryName={repositoryName}
      ariaLabels={ariaLabels}
      {...props}
    />
  )
}

const IssuePullRequestDescriptionItem = ({
  dataKey,
  metadataRef,
  showRepository = true,
  sortingItemSelected,
  statusCheckRollup,
  defaultRepositoryRender,
  defaultMetaRender,
  nameWithOwner,
  ariaLabels,
  getAuthorHref,
  id,
  includeGitDataFromMainQuery,
}: DescriptionProps & {
  defaultRepositoryRender: React.ReactNode
  defaultMetaRender: React.ReactNode
  nameWithOwner: string
  ariaLabels: {[id: string]: string}
}) => {
  const descriptionData = useFragment(descriptionFragment, dataKey as IssuePullRequestDescription$key)
  const {pull_request_single_subscription} = useFeatureFlags()

  const {variant} = useListViewVariant()

  const {
    author,
    reviewDecision,
    closed,
    closedAt: closedAtString,
    createdAt: createdAtString,
    updatedAt: updatedAtString,
    stateReason,
    milestone,
  } = descriptionData

  const authorType = author?.__typename
  const authorLogin = author?.login || VALUES.ghostUserLogin
  const authorFullLogin = authorType === 'Bot' ? `app/${authorLogin}` : authorLogin
  const isCopilot = author?.isCopilot
  const authorName = author?.name
  const displayName = isCopilot ? VALUES.copilotDisplayName : authorLogin
  const resourcePath = author?.resourcePath || ''
  const hovercardAttributes = hovercardAttributesForActor(authorLogin, {isCopilot})
  const createdAt = createdAtString ? new Date(createdAtString) : undefined
  const updatedAt = updatedAtString ? new Date(updatedAtString) : undefined
  const closedAt = closedAtString ? new Date(closedAtString) : undefined
  const loadStatusCheckFromMainQuery = pull_request_single_subscription && includeGitDataFromMainQuery
  return (
    <ListItemDescriptionItem
      data-testid={TEST_IDS.listRowRepoNameAndNumber}
      className={pullRequestDescriptionStyles.descriptionItem}
    >
      {variant === 'compact'
        ? showRepository && (
            <>
              <Truncate title={nameWithOwner} className={pullRequestDescriptionStyles.repositoryNameTruncate}>
                <span className={styles.compactNameWithOwnerLabel}>{nameWithOwner}</span>
              </Truncate>
              <span className="sr-only">{ariaLabels.number}</span>
            </>
          )
        : showRepository && defaultRepositoryRender}
      {defaultMetaRender}
      {variant === 'default' && (
        <>
          {closed ? (
            <TimestampContainer
              displayName={displayName}
              authorFullLogin={authorFullLogin}
              authorName={authorName}
              resourcePath={resourcePath}
              getAuthorHref={getAuthorHref}
              hovercardAttributes={hovercardAttributes}
              timestamp={closedAt}
              testId="closed-at"
              action={ActionType.Closed}
              isAgent={isCopilot ?? false}
            />
          ) : (
            <TimestampContainer
              displayName={displayName}
              authorFullLogin={authorFullLogin}
              authorName={authorName}
              resourcePath={resourcePath}
              getAuthorHref={getAuthorHref}
              hovercardAttributes={hovercardAttributes}
              timestamp={createdAt}
              testId="created-at"
              action={ActionType.Opened}
              isAgent={isCopilot ?? false}
            />
          )}
          {(sortingItemSelected === LABELS.RecentlyUpdated || sortingItemSelected === 'updated') && updatedAt && (
            <div data-testid="updated-at" className={styles.timestampContainer}>
              {' '}
              &middot; Updated{' '}
              <RelativeTime date={updatedAt} className={pullRequestDescriptionStyles.RelativeTime}>
                on {updatedAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
              </RelativeTime>
            </div>
          )}
        </>
      )}
      {stateReason?.toLowerCase() === 'duplicate' && <span data-testid="state-reason"> &middot;{' Duplicate'}</span>}
      {reviewDecision ? (
        <ReviewDecision decision={reviewDecision} variant={variant} />
      ) : (
        <LazyReviewDecision id={id} variant={variant} secondaryQueryRef={metadataRef} />
      )}
      {milestone && <MilestoneMetadata title={milestone.title} url={milestone.url} />}
      {statusCheckRollup ? (
        <CheckRunStatus variant={variant} statusCheckRollup={statusCheckRollup} />
      ) : (
        <LazyCheckRunStatus
          id={id}
          variant={variant}
          primaryQueryRef={loadStatusCheckFromMainQuery ? (dataKey as CheckRunStatusFromPullRequest$key) : undefined}
          secondaryQueryRef={metadataRef}
        />
      )}
    </ListItemDescriptionItem>
  )
}

const ActionType = {
  Opened: 'opened',
  Closed: 'closed',
} as const

type ActionType = (typeof ActionType)[keyof typeof ActionType]

const TimestampContainer = ({
  displayName,
  authorFullLogin,
  authorName,
  resourcePath,
  getAuthorHref,
  hovercardAttributes,
  timestamp,
  testId,
  action,
  isAgent,
}: {
  displayName: string
  authorFullLogin: string
  authorName: string | null | undefined
  resourcePath: string
  getAuthorHref?: (resourcePath: string) => string
  hovercardAttributes: HovercardAttributes
  timestamp?: Date
  testId: string
  action: ActionType
  isAgent: boolean
}) => (
  <div data-testid={testId} className={styles.timestampContainer}>
    <span>&middot; </span>
    <span>{action === ActionType.Closed && ' by '}</span>
    <Link
      href={getAuthorHref ? getAuthorHref(authorFullLogin) : resourcePath}
      className={styles.authorCreatedLink}
      {...hovercardAttributes}
    >
      {isFeatureEnabled('render_user_display_name') ? (
        <ProfileReference login={displayName} profileName={authorName} isAgent={isAgent} />
      ) : (
        displayName
      )}
    </Link>{' '}
    <span>{action === ActionType.Opened ? ` opened ` : ` was closed `}</span>
    {timestamp && (
      <RelativeTime date={timestamp}>
        on {timestamp.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
      </RelativeTime>
    )}
  </div>
)

try{ IssuePullRequestDescription.displayName ||= 'IssuePullRequestDescription' } catch {}
try{ IssuePullRequestDescriptionItem.displayName ||= 'IssuePullRequestDescriptionItem' } catch {}
try{ TimestampContainer.displayName ||= 'TimestampContainer' } catch {}